import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
// Supabase is loaded lazily inside the submit / magicLink handlers so the
// auth-js + storage-js SDK (~30 KiB) doesn't get hoisted into the shared
// main bundle on pages that don't need it.
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/hooks/use-auth";
import { useEffect } from "react";
import { pageTitle } from "@/lib/brand";

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
});

function safeRedirect(target: string | undefined): string {
  // Only allow same-origin relative paths starting with "/" and not "//"
  if (!target) return "/admin/temalar";
  if (!target.startsWith("/") || target.startsWith("//")) return "/admin/temalar";
  return target;
}

export const Route = createFileRoute("/login")({
  validateSearch: loginSearchSchema,
  head: () => ({
    meta: [
      { title: pageTitle("Giriş", "tr") },
      { name: "description", content: "Akıllı Sürdürülebilirlik üyelik ve yönetim paneli girişi — mevcut hesabınızla giriş yapın ya da yeni bir hesap oluşturun." },
      { property: "og:title", content: "Giriş — Akıllı Sürdürülebilirlik" },
      { property: "og:description", content: "Akıllı Sürdürülebilirlik üyelik ve yönetim paneli girişi — mevcut hesabınızla giriş yapın ya da yeni bir hesap oluşturun." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://akillisurdurulebilirlik.com/login" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const redirectTo = safeRedirect(search.redirect);
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: redirectTo });
  }, [loading, user, navigate, redirectTo]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + redirectTo },
        });
        if (error) throw error;
      }
      navigate({ to: redirectTo });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Hata");
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setErr("");
    const r = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + redirectTo,
    });
    if (r.error) setErr(r.error.message ?? "Google girişi başarısız");
  };

  const magicLink = async () => {
    setErr("");
    if (!email) {
      setErr("Önce e-posta adresinizi girin.");
      return;
    }
    setBusy(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin + redirectTo },
      });
      if (error) throw error;
      alert("Giriş bağlantısı e-posta adresinize gönderildi. Gelen kutunuzu kontrol edin.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Hata");
    } finally {
      setBusy(false);
    }
  };


  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <h1 className="font-serif text-4xl mb-2">{mode === "signin" ? "Giriş" : "Kayıt"}</h1>
      <p className="text-sm text-ink/60 mb-8">Yazı yönetim paneli için.</p>

      <button
        onClick={google}
        className="w-full mb-6 py-2.5 border border-ink/20 rounded-full text-sm hover:border-ink transition-colors"
      >
        Google ile devam et
      </button>

      <div className="flex items-center gap-3 mb-6 text-[10px] uppercase tracking-[0.2em] text-ink/85">
        <div className="h-px flex-1 bg-ink/10" /> veya <div className="h-px flex-1 bg-ink/10" />
      </div>

      <form onSubmit={submit} className="space-y-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@adresiniz.com"
          required
          className="w-full px-3 py-2 border border-border rounded bg-paper"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Şifre"
          required
          minLength={6}
          className="w-full px-3 py-2 border border-border rounded bg-paper"
        />
        {err && <p className="text-sm text-clay">{err}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full py-2.5 bg-ink text-paper rounded-full text-sm uppercase tracking-[0.2em] font-medium disabled:opacity-50"
        >
          {busy ? "..." : mode === "signin" ? "Giriş" : "Kayıt"}
        </button>
      </form>

      <button
        onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
        className="mt-4 text-xs text-ink/60 hover:text-ink block"
      >
        {mode === "signin" ? "Hesabınız yok mu? Kayıt olun" : "Zaten hesabınız var mı? Giriş yapın"}
      </button>

      <button
        onClick={magicLink}
        disabled={busy}
        className="mt-2 text-xs text-ink/60 hover:text-ink block disabled:opacity-50"
      >
        Şifreniz yok mu? E-posta ile giriş bağlantısı gönderin
      </button>

      <div className="mt-10 text-xs text-ink/85">
        <Link to="/">← Anasayfaya dön</Link>
      </div>
    </div>
  );
}
